import React, { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import { ScheduleFormFields, useScheduleFormState } from "./ScheduleTab";

const STATUS_OPTIONS = [
	{ value: "active", label: "Active" },
	{ value: "on hold", label: "On Hold" },
	{ value: "completed", label: "Completed" },
];

const STATUS_HELPER_TEXT = {
	"active": "This phase will be marked Active in BQE.",
	"on hold": "This phase will be marked On Hold in BQE.",
	"completed": "This phase will be marked Completed in BQE. If every other phase on this project is also Completed, its linked HubSpot deal will move to \"Finished Project.\" It will no longer appear in the Project Viewer list, and can only be reverted from the BQE website.",
};

// Combined Status + Schedule editor, opened from either the top nav or the
// Project Summary section — same dialog either way. Reuses the Schedule tab's
// form-state hook/fields so validation/derivation logic never drifts between
// the two dialogs.
export default function StatusScheduleDialog({
	open,
	onClose,
	projectName, // optional — shown under the dialog title so it's clear which project is being edited
	currentStatus, // "active" | "on hold" | "completed" | "" (normalized lowercase)
	rawStart,
	rawEndDate,
	onHoldRawStart,
	onHoldRawEndDate,
	onSaveStatus, // async (statusKey) => void
	onSaveSchedule, // async (start, durationWeeks, end) => void
	onSaveOnHoldSchedule, // async (start, durationWeeks, end) => void
	onLogFailure, // async (message) => void — best-effort, never throws
	onCompleted, // () => void — called after a successful save that results in status "completed"
}) {
	const [statusKey, setStatusKey] = useState(currentStatus || "active");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	const [confirmingComplete, setConfirmingComplete] = useState(false);

	const formState = useScheduleFormState(rawStart, rawEndDate, {
		rawStart: onHoldRawStart,
		rawEndDate: onHoldRawEndDate,
	}, { datesRequired: false });

	useEffect(() => {
		if (open) {
			setStatusKey(currentStatus || "active");
			formState.reset();
			setSaveError("");
			setConfirmingComplete(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const handleClose = () => {
		if (saving) return;
		setConfirmingComplete(false);
		onClose();
	};

	const doSave = async () => {
		setSaving(true);
		setSaveError("");
		try {
			if (statusKey !== (currentStatus || "active")) {
				await onSaveStatus(statusKey);
			}
			await onSaveSchedule(formState.draftStart, formState.draftDurationWeeks, formState.draftEndDate);
			if (formState.onHoldEnabled) {
				await onSaveOnHoldSchedule(formState.draftOnHoldStart, formState.draftOnHoldDurationWeeks, formState.draftOnHoldEndDate);
			} else if (formState.onHoldHadExisting) {
				await onSaveOnHoldSchedule(null, null, null);
			}
			setConfirmingComplete(false);
			onClose();
			// Completed phases drop out of the Project Viewer list entirely, so there's
			// nothing left here to keep showing — hand off to the parent to deselect.
			if (statusKey === "completed" && onCompleted) {
				onCompleted();
			}
		} catch (err) {
			const message = err?.response?.data?.error || err?.message || "Save failed.";
			setSaveError(message);
			if (onLogFailure) {
				try {
					await onLogFailure(message);
				} catch (_) {
					// Logging is best-effort — never let a logging failure mask the real error.
				}
			}
		} finally {
			setSaving(false);
		}
	};

	const handleSaveClick = () => {
		if (!formState.validateAll()) return;
		if (statusKey === "completed" && (currentStatus || "active") !== "completed") {
			setConfirmingComplete(true);
			return;
		}
		doSave();
	};

	return (
		<>
			<Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
				<DialogTitle className="scheduleEditDialogTitle">
					Edit Status &amp; Schedule
					{projectName && (
						<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
							{projectName}
						</Typography>
					)}
				</DialogTitle>
				<DialogContent className="scheduleEditDialogContent">
					<ToggleButtonGroup
						value={statusKey}
						exclusive
						size="small"
						fullWidth
						color="primary"
						onChange={(e, val) => { if (val) setStatusKey(val); }}
					>
						{STATUS_OPTIONS.map((opt) => (
							<ToggleButton key={opt.value} value={opt.value}>{opt.label}</ToggleButton>
						))}
					</ToggleButtonGroup>

					<Typography
						variant="caption"
						color={statusKey === "completed" ? "error" : "text.secondary"}
						sx={{ display: "block", mt: 1, mb: 1 }}
					>
						{STATUS_HELPER_TEXT[statusKey]}
					</Typography>

					<Divider sx={{ my: 1 }} />

					<ScheduleFormFields
						fieldLabels={{ start: "Start Date", duration: "Duration", end: "End Date" }}
						onHold={{ fieldLabels: { start: "On Hold Start Date", duration: "On Hold Duration", end: "On Hold End Date" } }}
						state={formState}
					/>

					{saveError && (
						<Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>
							{saveError}
						</Typography>
					)}
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={handleClose} disabled={saving}>
							Cancel
						</Button>
						<Tooltip
							arrow
							placement="top"
							title={!formState.allFilled ? "Fix the highlighted date fields above." : ""}
						>
							<span>
								<Button
									variant="contained"
									color="primary"
									disableElevation
									onClick={handleSaveClick}
									disabled={saving || !formState.allFilled}
									startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
								>
									{saving ? "Saving…" : "Save"}
								</Button>
							</span>
						</Tooltip>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={confirmingComplete} onClose={() => !saving && setConfirmingComplete(false)} maxWidth="xs" fullWidth>
				<DialogTitle>Mark phase as Completed?</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 1.5 }}>
						Once this phase is marked Completed, it will no longer appear in the Project Viewer list. Its linked HubSpot deal will also be moved to the "Finished Project" stage, but only once every other phase on this project is Completed too.
					</Typography>
					<Typography variant="body2" sx={{ mb: 1.5 }}>
						To revert it back to Active or On Hold, you'll need to change its status directly on the BQE website, and update the deal stage on the HubSpot site as well. This can't be undone from here.
					</Typography>
					<Typography variant="body2" sx={{ fontWeight: 600, mb: 2.5 }}>
						Are you sure you want to continue?
					</Typography>
					{saveError && (
						<Typography variant="caption" color="error" sx={{ display: "block", mt: 1.5 }}>
							{saveError}
						</Typography>
					)}
					<div className="dialogButtons">
						<Button variant="outlined" color="primary" onClick={() => setConfirmingComplete(false)} disabled={saving}>
							Cancel
						</Button>
						<Button
							variant="contained"
							color="error"
							disableElevation
							onClick={doSave}
							disabled={saving}
							startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
						>
							{saving ? "Saving…" : "Yes, mark Completed"}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
